import { useCallback, useState } from 'react';

export type UploadStatus =
  // Bytes are still being sent to the server.
  | { phase: 'uploading'; fileName: string; percent: number }
  // Every byte is sent; the server is still storing the file before it responds.
  | { phase: 'finishing'; fileName: string }
  // Stays until the next upload starts, so the user sees the upload finished.
  | { phase: 'complete'; fileName: string }
  | null;

export type TrackUpload = <T>(
  fileName: string,
  run: (onProgress: (percent: number) => void) => Promise<T>
) => Promise<T>;

interface UseUploadProgressReturn {
  status: UploadStatus;
  isInFlight: boolean;
  track: TrackUpload;
}

/**
 * Tracks the lifecycle of one upload at a time for a file field.
 *
 * `track(fileName, run)` hands `run` an `onProgress(percent)` callback to pass
 * to the upload call, and resolves/rejects with whatever `run` does. On failure
 * the status clears — the field shows its own error message.
 */
export function useUploadProgress(): UseUploadProgressReturn {
  const [status, setStatus] = useState<UploadStatus>(null);

  const track = useCallback<TrackUpload>(async (fileName, run) => {
    setStatus({ phase: 'uploading', fileName, percent: 0 });
    try {
      const result = await run(percent =>
        setStatus(
          percent >= 100
            ? { phase: 'finishing', fileName }
            : { phase: 'uploading', fileName, percent }
        )
      );
      setStatus({ phase: 'complete', fileName });
      return result;
    } catch (err) {
      setStatus(null);
      throw err;
    }
  }, []);

  const isInFlight =
    status?.phase === 'uploading' || status?.phase === 'finishing';

  return { status, isInFlight, track };
}
